import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { Save, Plus, Trash2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem('auth_token') || '';

/* ── Input style helper ── */
const inp = (extra = {}) => ({
  width: '100%', padding: '7px 10px', border: '1px solid rgba(221,212,197,0.7)',
  borderRadius: 7, fontSize: 12, boxSizing: 'border-box', background: '#fff',
  ...extra,
});

/* ── Field label ── */
function FL({ children }) {
  return <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: '#66706D', marginBottom: 3 }}>{children}</div>;
}

/* ── Reusable featured-card slot editor (used for story_1, story_2, and zone3) ── */
function FeaturedCardEditor({ label, value = {}, onChange, slotIndex }) {
  const u = (field, v) => onChange({ ...value, [field]: v });
  const bgOptions = ['#0d1f30', '#0c1e18', '#1a1028', '#111508', '#081D30', '#0a1a24'];

  return (
    <div style={{ background: 'rgba(248,246,240,0.6)', border: '1px solid rgba(221,212,197,0.6)', borderRadius: 10, padding: 16 }} data-testid={`mm-slot-editor-${slotIndex}`}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.09em', color: '#07121f' }}>{label}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <FL>Sort</FL>
          <input type="number" value={value.sort_order ?? slotIndex} onChange={e => u('sort_order', parseInt(e.target.value) || slotIndex)}
            style={{ ...inp(), width: 52, padding: '5px 8px' }} data-testid={`mm-slot-sort-${slotIndex}`} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={value.is_active !== false} onChange={e => u('is_active', e.target.checked)}
              style={{ accentColor: '#008C86' }} data-testid={`mm-slot-active-${slotIndex}`} />
            <span style={{ fontWeight: 600, color: value.is_active !== false ? '#008C86' : '#6B7280' }}>
              {value.is_active !== false ? 'Active' : 'Inactive'}
            </span>
          </label>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* Title */}
        <div><FL>Title *</FL>
          <input value={value.title || ''} onChange={e => u('title', e.target.value)}
            placeholder="Card title" style={inp()} data-testid={`mm-slot-title-${slotIndex}`} />
        </div>

        {/* Subtitle */}
        <div><FL>Subtitle</FL>
          <input value={value.subtitle || ''} onChange={e => u('subtitle', e.target.value)}
            placeholder="Short subtitle or eyebrow label" style={inp()} data-testid={`mm-slot-subtitle-${slotIndex}`} />
        </div>

        {/* Description */}
        <div><FL>Description</FL>
          <textarea value={value.description || ''} onChange={e => u('description', e.target.value)}
            placeholder="Optional description" rows={2} style={{ ...inp(), resize: 'vertical', lineHeight: 1.4 }}
            data-testid={`mm-slot-description-${slotIndex}`} />
        </div>

        {/* Image URL */}
        <div><FL>Image URL (ImageKit)</FL>
          <input value={value.image_url || ''} onChange={e => u('image_url', e.target.value)}
            placeholder="https://ik.imagekit.io/…" style={inp()} data-testid={`mm-slot-image-url-${slotIndex}`} />
        </div>

        {/* Bg color fallback (only for story boxes) */}
        {value.bg_color !== undefined && (
          <div><FL>Background color (fallback)</FL>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              {bgOptions.map(c => (
                <button key={c} onClick={() => u('bg_color', c)} title={c}
                  style={{ width: 22, height: 22, borderRadius: 5, background: c, border: value.bg_color === c ? '2px solid #008C86' : '1px solid rgba(0,0,0,.2)', cursor: 'pointer' }} />
              ))}
              <input value={value.bg_color || ''} onChange={e => u('bg_color', e.target.value)}
                placeholder="#0d1f30" style={{ ...inp(), width: 90, padding: '4px 8px' }} />
            </div>
          </div>
        )}

        {/* CTA */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div><FL>CTA Label</FL>
            <input value={value.cta_label || ''} onChange={e => u('cta_label', e.target.value)}
              placeholder="e.g. Read more" style={inp()} data-testid={`mm-slot-cta-label-${slotIndex}`} />
          </div>
          <div><FL>Link Type</FL>
            <select value={value.link_type || 'internal'} onChange={e => u('link_type', e.target.value)}
              style={{ ...inp(), cursor: 'pointer' }} data-testid={`mm-slot-link-type-${slotIndex}`}>
              <option value="internal">Internal route</option>
              <option value="external">External URL</option>
              <option value="youtube">YouTube</option>
              <option value="video">Video (generic)</option>
              <option value="document">Document</option>
              <option value="resource">Resource</option>
            </select>
          </div>
        </div>

        {/* CTA URL (internal) */}
        <div><FL>CTA URL (internal route, e.g. /magazine)</FL>
          <input value={value.cta_url || ''} onChange={e => u('cta_url', e.target.value)}
            placeholder="/subscribe, /magazine, /career-hub…" style={inp()} data-testid={`mm-slot-cta-url-${slotIndex}`} />
        </div>

        {/* External URL */}
        <div><FL>External URL</FL>
          <input value={value.external_url || ''} onChange={e => u('external_url', e.target.value)}
            placeholder="https://…" style={inp()} data-testid={`mm-slot-external-url-${slotIndex}`} />
        </div>

        {/* YouTube URL */}
        <div><FL>YouTube URL</FL>
          <input value={value.youtube_url || ''} onChange={e => u('youtube_url', e.target.value)}
            placeholder="https://youtube.com/watch?v=…" style={inp()} data-testid={`mm-slot-youtube-url-${slotIndex}`} />
        </div>

        {/* Generic Video URL */}
        <div><FL>Video URL (generic host)</FL>
          <input value={value.video_url || ''} onChange={e => u('video_url', e.target.value)}
            placeholder="https://vimeo.com/… or other video host" style={inp()} data-testid={`mm-slot-video-url-${slotIndex}`} />
        </div>

        {/* Document URL */}
        <div><FL>Document URL</FL>
          <input value={value.document_url || ''} onChange={e => u('document_url', e.target.value)}
            placeholder="https://…/resource.pdf" style={inp()} data-testid={`mm-slot-document-url-${slotIndex}`} />
        </div>

        {/* Resource URL */}
        <div><FL>Resource URL</FL>
          <input value={value.resource_url || ''} onChange={e => u('resource_url', e.target.value)}
            placeholder="https://…/resource or gated resource link" style={inp()} data-testid={`mm-slot-resource-url-${slotIndex}`} />
        </div>

        {/* Custom thumbnail */}
        <div style={{ background: 'rgba(0,140,134,.05)', border: '1px solid rgba(0,140,134,.15)', borderRadius: 7, padding: '10px 12px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, cursor: 'pointer', fontWeight: 600, color: '#07121f', marginBottom: 6 }}>
            <input type="checkbox" checked={!!value.use_custom_thumbnail} onChange={e => u('use_custom_thumbnail', e.target.checked)}
              style={{ accentColor: '#008C86' }} data-testid={`mm-slot-use-custom-thumb-${slotIndex}`} />
            Use custom thumbnail
          </label>
          <div style={{ fontSize: 10, color: '#66706D', marginBottom: 7, lineHeight: 1.4 }}>
            Overrides the default card image. Use this for PDFs, videos, external links, and resources that need a clean manual preview.
          </div>
          <input value={value.thumbnail_image_url || ''} onChange={e => u('thumbnail_image_url', e.target.value)}
            placeholder="Custom Thumbnail ImageKit URL — https://ik.imagekit.io/…"
            style={{ ...inp(), opacity: value.use_custom_thumbnail ? 1 : 0.45 }}
            disabled={!value.use_custom_thumbnail}
            data-testid={`mm-slot-thumbnail-url-${slotIndex}`} />
          {value.use_custom_thumbnail && value.thumbnail_image_url && (
            <img src={value.thumbnail_image_url} alt="thumbnail preview"
              style={{ width: '100%', height: 64, objectFit: 'cover', borderRadius: 6, marginTop: 6, border: '1px solid rgba(0,140,134,.2)' }}
              onError={e => { e.target.style.display = 'none'; }} />
          )}
        </div>

        {/* Main image preview (when no custom thumbnail) */}
        {!value.use_custom_thumbnail && value.image_url && (
          <img src={value.image_url} alt="preview"
            style={{ width: '100%', height: 72, objectFit: 'cover', borderRadius: 7, border: '1px solid rgba(221,212,197,0.5)' }}
            onError={e => { e.target.style.display = 'none'; }} />
        )}
      </div>
    </div>
  );
}

/* ── Nav link editor (zone1 / zone2 — unchanged) ── */
function LinkEditor({ links, onChange }) {
  const add = () => onChange([...links, { label: '', url: '', active: true }]);
  const remove = (i) => { const l = [...links]; l.splice(i, 1); onChange(l); };
  const update = (i, field, value) => { const l = [...links]; l[i] = { ...l[i], [field]: value }; onChange(l); };

  return (
    <div>
      {links.map((link, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
          <input value={link.label} onChange={e => update(i, 'label', e.target.value)} placeholder="Label"
            style={{ flex: 1, padding: '6px 9px', border: '1px solid rgba(221,212,197,0.7)', borderRadius: 7, fontSize: 12 }} />
          <input value={link.url} onChange={e => update(i, 'url', e.target.value)} placeholder="/path or https://…"
            style={{ flex: 2, padding: '6px 9px', border: '1px solid rgba(221,212,197,0.7)', borderRadius: 7, fontSize: 12 }} />
          <button onClick={() => update(i, 'active', !link.active)} title={link.active ? 'Disable' : 'Enable'}
            style={{ background: 'none', border: '1px solid rgba(221,212,197,0.7)', borderRadius: 7, padding: '5px 7px', cursor: 'pointer', color: link.active ? '#008C86' : '#66706D' }}>
            {link.active ? <Eye size={12} /> : <EyeOff size={12} />}
          </button>
          <button onClick={() => remove(i)}
            style={{ background: 'none', border: '1px solid rgba(220,38,38,.2)', borderRadius: 7, padding: '5px 7px', cursor: 'pointer', color: '#DC2626' }}>
            <Trash2 size={12} />
          </button>
        </div>
      ))}
      <button onClick={add} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 11px', background: 'rgba(0,140,134,.08)', color: '#008C86', border: 'none', borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: 'pointer', marginTop: 2 }}>
        <Plus size={11} /> Add Link
      </button>
    </div>
  );
}

/* ── Zone3 feature card editor — uses feature_* field naming (backward compat) ── */
function Zone3Editor({ value = {}, onChange }) {
  const u = (field, v) => onChange({ ...value, [field]: v });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div><FL>Active</FL>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
          <input type="checkbox" checked={value.feature_active !== false} onChange={e => u('feature_active', e.target.checked)} style={{ accentColor: '#008C86' }} data-testid="mm-z3-active" />
          <span style={{ fontWeight: 600, color: value.feature_active !== false ? '#008C86' : '#6B7280' }}>{value.feature_active !== false ? 'Active' : 'Inactive'}</span>
        </label>
      </div>
      <div><FL>Sort Order</FL>
        <input type="number" value={value.feature_sort_order ?? 3} onChange={e => u('feature_sort_order', parseInt(e.target.value) || 3)} style={{ ...inp(), width: 70, padding: '5px 8px' }} data-testid="mm-z3-sort" />
      </div>
      <div><FL>Image URL (ImageKit)</FL>
        <input value={value.feature_image_url || ''} onChange={e => u('feature_image_url', e.target.value)} placeholder="https://ik.imagekit.io/…" style={inp()} data-testid="mm-z3-image-url" />
      </div>
      <div><FL>Eyebrow Label</FL>
        <input value={value.feature_label || ''} onChange={e => u('feature_label', e.target.value)} placeholder="e.g. Opportunity Post" style={inp()} data-testid="mm-z3-label" />
      </div>
      <div><FL>Title</FL>
        <input value={value.feature_title || ''} onChange={e => u('feature_title', e.target.value)} placeholder="Feature title" style={inp()} data-testid="mm-z3-title" />
      </div>
      <div><FL>Subtitle</FL>
        <input value={value.feature_subtitle || ''} onChange={e => u('feature_subtitle', e.target.value)} placeholder="e.g. Education · Innovation · Opportunity" style={inp()} data-testid="mm-z3-subtitle" />
      </div>
      <div><FL>Description</FL>
        <textarea value={value.feature_description || ''} onChange={e => u('feature_description', e.target.value)} placeholder="Optional description" rows={2} style={{ ...inp(), resize: 'vertical' }} data-testid="mm-z3-description" />
      </div>
      <div><FL>CTA Label</FL>
        <input value={value.feature_cta_label || ''} onChange={e => u('feature_cta_label', e.target.value)} placeholder="e.g. Read current issue" style={inp()} data-testid="mm-z3-cta-label" />
      </div>
      <div><FL>Link Type</FL>
        <select value={value.feature_link_type || 'internal'} onChange={e => u('feature_link_type', e.target.value)} style={{ ...inp(), cursor: 'pointer' }} data-testid="mm-z3-link-type">
          <option value="internal">Internal route</option>
          <option value="external">External URL</option>
          <option value="youtube">YouTube</option>
          <option value="video">Video (generic)</option>
          <option value="document">Document</option>
          <option value="resource">Resource</option>
        </select>
      </div>
      <div><FL>CTA URL (internal route)</FL>
        <input value={value.feature_url || ''} onChange={e => u('feature_url', e.target.value)} placeholder="/magazine" style={inp()} data-testid="mm-z3-cta-url" />
      </div>
      <div><FL>External URL</FL>
        <input value={value.feature_external_url || ''} onChange={e => u('feature_external_url', e.target.value)} placeholder="https://…" style={inp()} data-testid="mm-z3-external-url" />
      </div>
      <div><FL>YouTube URL</FL>
        <input value={value.feature_youtube_url || ''} onChange={e => u('feature_youtube_url', e.target.value)} placeholder="https://youtube.com/watch?v=…" style={inp()} data-testid="mm-z3-youtube-url" />
      </div>
      <div><FL>Video URL (generic host)</FL>
        <input value={value.feature_video_url || ''} onChange={e => u('feature_video_url', e.target.value)} placeholder="https://vimeo.com/…" style={inp()} data-testid="mm-z3-video-url" />
      </div>
      <div><FL>Document URL</FL>
        <input value={value.feature_document_url || ''} onChange={e => u('feature_document_url', e.target.value)} placeholder="https://…/resource.pdf" style={inp()} data-testid="mm-z3-document-url" />
      </div>
      <div><FL>Resource URL</FL>
        <input value={value.feature_resource_url || ''} onChange={e => u('feature_resource_url', e.target.value)} placeholder="https://…/resource" style={inp()} data-testid="mm-z3-resource-url" />
      </div>

      {/* Custom thumbnail */}
      <div style={{ background: 'rgba(0,140,134,.05)', border: '1px solid rgba(0,140,134,.15)', borderRadius: 7, padding: '10px 12px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, cursor: 'pointer', fontWeight: 600, color: '#07121f', marginBottom: 6 }}>
          <input type="checkbox" checked={!!value.feature_use_custom_thumbnail} onChange={e => u('feature_use_custom_thumbnail', e.target.checked)}
            style={{ accentColor: '#008C86' }} data-testid="mm-z3-use-custom-thumb" />
          Use custom thumbnail
        </label>
        <div style={{ fontSize: 10, color: '#66706D', marginBottom: 7, lineHeight: 1.4 }}>
          Overrides the default card image. Use this for PDFs, videos, external links, and resources that need a clean manual preview.
        </div>
        <input value={value.feature_thumbnail_image_url || ''} onChange={e => u('feature_thumbnail_image_url', e.target.value)}
          placeholder="Custom Thumbnail ImageKit URL — https://ik.imagekit.io/…"
          style={{ ...inp(), opacity: value.feature_use_custom_thumbnail ? 1 : 0.45 }}
          disabled={!value.feature_use_custom_thumbnail}
          data-testid="mm-z3-thumbnail-url" />
        {value.feature_use_custom_thumbnail && value.feature_thumbnail_image_url && (
          <img src={value.feature_thumbnail_image_url} alt="thumbnail preview"
            style={{ width: '100%', height: 64, objectFit: 'cover', borderRadius: 6, marginTop: 6, border: '1px solid rgba(0,140,134,.2)' }}
            onError={e => { e.target.style.display = 'none'; }} />
        )}
      </div>

      {!value.feature_use_custom_thumbnail && value.feature_image_url && (
        <img src={value.feature_image_url} alt="preview"
          style={{ width: '100%', height: 72, objectFit: 'cover', borderRadius: 7, border: '1px solid rgba(221,212,197,0.5)' }}
          onError={e => { e.target.style.display = 'none'; }} />
      )}
    </div>
  );
}

/* ── Per-menu editor ── */
const DEFAULT_CONFIG = (menuKey) => ({
  menu_key: menuKey,
  is_active: true,
  zone1: { heading: 'Navigation', links: [] },
  zone2: { heading: 'Sub-links', links: [] },
  zone3: {
    feature_image_url: '', feature_label: 'Opportunity Post', feature_title: 'The Future of Work',
    feature_url: '/magazine', feature_active: true, feature_subtitle: 'Education · Innovation · Opportunity',
    feature_description: '', feature_cta_label: 'Read current issue',
    feature_document_url: '', feature_youtube_url: '', feature_external_url: '',
    feature_video_url: '', feature_resource_url: '',
    feature_link_type: 'internal', feature_sort_order: 3,
    feature_use_custom_thumbnail: false, feature_thumbnail_image_url: '',
  },
  story_1: {
    title: '', subtitle: '', description: '', image_url: '', bg_color: '#0d1f30',
    cta_label: 'Read more', cta_url: '', external_url: '', youtube_url: '',
    video_url: '', document_url: '', resource_url: '',
    link_type: 'internal', is_active: true, sort_order: 1,
    use_custom_thumbnail: false, thumbnail_image_url: '',
  },
  story_2: {
    title: '', subtitle: '', description: '', image_url: '', bg_color: '#0c1e18',
    cta_label: 'Read more', cta_url: '', external_url: '', youtube_url: '',
    video_url: '', document_url: '', resource_url: '',
    link_type: 'internal', is_active: true, sort_order: 2,
    use_custom_thumbnail: false, thumbnail_image_url: '',
  },
});

function MenuEditor({ menuKey, label }) {
  const [config, setConfig] = useState(DEFAULT_CONFIG(menuKey));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/mega-menu/${menuKey}`);
        const data = await res.json();
        if (data && (data.zone1 || data.story_1)) {
          setConfig({ ...DEFAULT_CONFIG(menuKey), ...data });
        }
      } catch { /* keep defaults */ }
      finally { setLoading(false); }
    })();
  }, [menuKey]);

  const save = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/mega-menu/${menuKey}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });
      toast.success(`${label} menu saved`);
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <div style={{ padding: 24, color: '#66706D' }}>Loading…</div>;

  return (
    <div style={{ background: '#fff', border: '1px solid rgba(221,212,197,0.7)', borderRadius: 12, padding: 22, marginBottom: 24 }} data-testid={`mm-editor-${menuKey}`}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h2 style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: 16, fontWeight: 700, color: '#07121f', margin: 0 }}>{label} Menu</h2>
          <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={config.is_active} onChange={e => setConfig({ ...config, is_active: e.target.checked })} style={{ accentColor: '#008C86' }} />
            <span style={{ fontWeight: 600, color: config.is_active ? '#008C86' : '#6B7280' }}>{config.is_active ? 'Active' : 'Disabled'}</span>
          </label>
        </div>
        <button onClick={save} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 16px', background: '#081D30', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
          <Save size={12} /> {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      {/* Nav Zones (zone1 / zone2) */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 22 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: '#66706D', marginBottom: 8 }}>Zone 1 — Navigation</div>
          <input value={config.zone1?.heading || ''} onChange={e => setConfig({ ...config, zone1: { ...config.zone1, heading: e.target.value } })} placeholder="Section heading" style={{ ...inp(), marginBottom: 8 }} />
          <LinkEditor links={config.zone1?.links || []} onChange={links => setConfig({ ...config, zone1: { ...config.zone1, links } })} />
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: '#66706D', marginBottom: 8 }}>Zone 2 — Sub-links</div>
          <input value={config.zone2?.heading || ''} onChange={e => setConfig({ ...config, zone2: { ...config.zone2, heading: e.target.value } })} placeholder="Section heading" style={{ ...inp(), marginBottom: 8 }} />
          <LinkEditor links={config.zone2?.links || []} onChange={links => setConfig({ ...config, zone2: { ...config.zone2, links } })} />
        </div>
      </div>

      {/* 6 Featured Card Slots divider */}
      <div style={{ borderTop: '1px solid rgba(221,212,197,0.6)', paddingTop: 18 }}>
        <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.1em', color: '#008C86', marginBottom: 14 }}>
          Featured Card Slots — {label} Menu (3 slots)
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
          {/* Story Slot 1 */}
          <FeaturedCardEditor
            label="Top Story Slot 1"
            value={config.story_1}
            onChange={v => setConfig({ ...config, story_1: v })}
            slotIndex={`${menuKey}-s1`}
          />
          {/* Story Slot 2 */}
          <FeaturedCardEditor
            label="Top Story Slot 2"
            value={config.story_2}
            onChange={v => setConfig({ ...config, story_2: v })}
            slotIndex={`${menuKey}-s2`}
          />
          {/* Zone 3 — Magazine Feature Card */}
          <div style={{ background: 'rgba(248,246,240,0.6)', border: '1px solid rgba(221,212,197,0.6)', borderRadius: 10, padding: 16 }} data-testid={`mm-slot-editor-${menuKey}-z3`}>
            <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.09em', color: '#07121f', marginBottom: 12 }}>Magazine Feature Card</div>
            <Zone3Editor value={config.zone3} onChange={z3 => setConfig({ ...config, zone3: z3 })} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Page ── */
export default function AdminMegaMenuPage() {
  return (
    <AdminLayout title="Site Controls" breadcrumb="Mega Menu">
      <div style={{ maxWidth: 1160 }}>
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: 20, fontWeight: 800, color: '#07121f', letterSpacing: '-.05em', margin: '0 0 4px' }}>
            Mega Menu CMS
          </h1>
          <p style={{ fontSize: 13, color: '#66706D', margin: 0 }}>
            6 featured card slots (3 per menu). Each slot is independently editable. Changes reflect on the public site mega menu.
          </p>
        </div>
        <MenuEditor menuKey="newsroom" label="Newsroom" />
        <MenuEditor menuKey="about" label="About" />
      </div>
    </AdminLayout>
  );
}
